/**
 * PricingAdapter - Compatibility layer between old NestingController and new PricingRegistry
 *
 * Purpose:
 * - Translates old cost property calls to new registry methods
 * - Allows incremental migration without rewriting 2800 lines of code
 * - Maintains backward compatibility while new system absorbs functionality
 *
 * Strategy:
 * Instead of: this.costAcrylicDoor = [{ cost: 50000 }, ...]
 * Use adapter: adapter.addDoorCost('acrylic', { cost: 50000 })
 *
 * The adapter:
 * 1. Stores costs in new PricingRegistry
 * 2. Provides getter/setter for old property names (e.g., costAcrylicDoor)
 * 3. Aggregates costs using new PricingCalculator
 * 4. Allows old code to work unchanged
 */

import PricingRegistry from './PricingRegistry';
import PricingCalculator from './PricingCalculator';
import PricingConsistencyValidator from './pricingConsistencyValidator';
// import * as Sentry from '@sentry/react';

export class PricingAdapter {
  /**
   * Initialize adapter with separate registries for cost and sale prices
   * @param {object} ruleConfig - Rules for conditional pricing
   * @param {object} priceConfig - Default cost prices
   * @param {object} profitConfig - Default profit margins
   */
  constructor({ priceConfig, profitConfig, ruleConfig }) {
    this.registryCostPrice = new PricingRegistry();
    this.registrySalePrice = new PricingRegistry();

    this.calculator = new PricingCalculator({
      ruleConfig,
      priceConfig,
      profitConfig,
    });
    this.pricingConsistencyValidator = new PricingConsistencyValidator({
      ruleConfig,
      priceConfig,
      profitConfig,
    });
  }

  /**
   * Import cost and sale data from persistable format into registries
   * @param {object} costPersistable - Persistable data for cost registry
   * @param {object} salePersistable - Persistable data for sale registry
   * Validates consistency after import and logs any issues to Sentry
   */
  importFromPersistable(costPersistable, salePersistable) {
    if (costPersistable) {
      this.registryCostPrice =
        PricingRegistry.fromPersistable(costPersistable) ??
        this.registryCostPrice;
    }
    if (salePersistable) {
      this.registrySalePrice =
        PricingRegistry.fromPersistable(salePersistable) ??
        this.registrySalePrice;
    }
    this.validatePricingConsistency();
  }

  /**
   * Add cost item to registry with default price/profit auto-fill
   * Stores in both cost and sale price registries if margin provided
   * @param {string} category - Component category
   * @param {string} type - Component type
   * @param {object} cost - Cost object { unitPrice, profit, ... }
   * @param {object} costMargin - Optional sale price margin object
   * @param {string} subtype - Optional subtype for nested categories
   */
  addCost(category, type, cost, costMargin, subtype) {
    if (!cost) return;
    const defaultPrice = this.calculator.getDefaultPrice(
      category,
      type,
      subtype
    );
    const defaultProfit = this.calculator.getDefaultProfit(
      category,
      type,
      subtype
    );
    if (defaultPrice !== undefined && cost.unitPrice === undefined) {
      cost.unitPrice = JSON.stringify(defaultPrice);
    }
    if (defaultProfit !== undefined && cost.profit === undefined) {
      cost.profit = JSON.stringify(defaultProfit);
    }
    this.registryCostPrice.addCost(category, type, cost, subtype);
    if (costMargin !== undefined) {
      this.registrySalePrice.addCost(category, type, costMargin, subtype);
    }
    return this;
  }

  /**
   * Update profit margin globally
   * Affects all future price calculations
   * @param {number} newMargin - New profit margin (0-1 range, e.g., 0.4 = 40%)
   */
  updateProfitMargin(newMargin) {
    this.calculator.profitMargin = newMargin;
  }

  /**
   * Clear all accumulated costs from both registries
   * Resets to initial empty state
   */
  clearCosts() {
    this.registryCostPrice.reset();
    this.registrySalePrice.reset();
  }

  /**
   * Get aggregated cost totals across all categories
   * @returns {object} Totals: { doors, materials, component, accessory, labor, special, grand }
   */
  getAggregatedTotals() {
    return this.calculator.aggregateCosts(this.registryCostPrice.getRegistry());
  }

  /**
   * Get aggregated sale price totals across all categories
   * @returns {object} Totals: { doors, materials, component, accessory, labor, special, grand }
   */
  getAggregatedSaleTotals() {
    return this.calculator.aggregateCosts(this.registrySalePrice.getRegistry());
  }

  /**
   * Get detailed breakdown of costs by subcategory
   * @param {object} registry - Optional custom registry (defaults to cost registry)
   * @returns {object} Breakdown with item counts and totals
   */
  getDetailedBreakdown(registry = this.registryCostPrice.getRegistry()) {
    return this.calculator.getDetailedBreakdown(registry);
  }

  /**
   * Get detailed breakdown of sale prices by subcategory
   * @param {object} registry - Optional custom registry (defaults to sale registry)
   * @returns {object} Breakdown with item counts and totals
   */
  getDetailedBreakdownSale(registry = this.registrySalePrice.getRegistry()) {
    return this.calculator.getDetailedBreakdown(registry);
  }

  /**
   * Check if either registry has unsaved changes
   * @param {boolean} isCost - Check cost registry (true) or sale registry (false)
   * @returns {boolean} True if registry has changes
   */
  hasChanges(isCost = true) {
    return isCost
      ? this.registryCostPrice.hasChanges()
      : this.registrySalePrice.hasChanges();
  }

  /**
   * Get registry instance for advanced direct access
   * @param {boolean} isCost - Get cost registry (true) or sale registry (false)
   * @returns {PricingRegistry} Registry instance
   */
  getRegistry(isCost = true) {
    return isCost ? this.registryCostPrice : this.registrySalePrice;
  }

  /**
   * Get cost registry instance (backward compatibility)
   * @returns {PricingRegistry}
   */
  getRegistryCost() {
    return this.getRegistry(true);
  }

  /**
   * Get sale registry instance (backward compatibility)
   * @returns {PricingRegistry}
   */
  getRegistrySale() {
    return this.getRegistry(false);
  }

  /**
   * Get calculator instance for direct calculations
   * @returns {PricingCalculator}
   */
  getCalculator() {
    return this.calculator;
  }

  /**
   * Check if cost registry has unsaved changes (backward compatibility)
   * @returns {boolean} True if cost registry has changes
   */
  hasChangesCost() {
    return this.hasChanges(true);
  }

  /**
   * Check if sale registry has unsaved changes (backward compatibility)
   * @returns {boolean} True if sale registry has changes
   */
  hasChangesSale() {
    return this.hasChanges(false);
  }

  /**
   * Get cache performance statistics from calculator
   * Shows hit count and currently cached keys
   * @returns {object} Cache stats: { size, entries }
   */
  getCacheStats() {
    return this.calculator.getCacheStats();
  }

  validatePricingConsistency() {
    const report = this.pricingConsistencyValidator.validateAll();
    if (!report.checks.every(c => c.status === 'pass')) {
      console.error('❌ Pricing structure inconsistent!', report);
      // Sentry.captureMessage('Pricing structure inconsistent', {
      //   level: 'error',
      //   extra: { report },
      // });
    } else {
      console.log('✅ Pricing structure consistent', report);
    }
  }
}

export default PricingAdapter;
